import { router, usePage } from "@inertiajs/react";
import { Loader2, Pencil, ShoppingCart, User as UserIcon, X } from "lucide-react";
import { type ReactNode, useCallback, useEffect, useMemo, useState } from "react";

import AsyncCombobox, { type AsyncOption } from "@/components/async-combobox";
import { Button } from "@/components/ui/button";
import { ButtonGroup } from "@/components/ui/button-group";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import type { PurchaseRequest, PurchaseRequestAssignments, SharedData, UserLite } from "@/types";

const ROLE_METADATA = {
  requester: {
    label: "Solicitante",
    description: "Persona que inició la solicitud",
    icon: UserIcon,
  },
  buyer: {
    label: "Comprador",
    description: "Responsable de gestionar la compra",
    icon: ShoppingCart,
  },
} as const;

type RoleKey = keyof typeof ROLE_METADATA;

interface PurchaseRequestAssignmentsManagerProps {
  purchaseRequest: PurchaseRequest;
  assignments: PurchaseRequestAssignments;
  canEdit: boolean;
  leadingAction?: ReactNode;
}

export default function PurchaseRequestAssignmentsManager({
  purchaseRequest,
  assignments,
  canEdit,
  leadingAction,
}: PurchaseRequestAssignmentsManagerProps) {
  const [open, setOpen] = useState(false);
  const { auth } = usePage<SharedData>().props;
  const organizationSlug = auth.current_organization?.slug ?? null;

  const summaryButtons = useMemo(() => {
    return (Object.keys(ROLE_METADATA) as RoleKey[]).map((role) => {
      const currentUser = assignments[role];
      const { label, icon: Icon } = ROLE_METADATA[role];
      const userLabel = formatUserLabel(currentUser);

      return (
        <Button
          key={role}
          type="button"
          variant="outline"
          size="sm"
          disabled
          className="gap-2 disabled:opacity-100"
          title={`${label}: ${userLabel}`}
        >
          <Icon className="h-3.5 w-3.5" />
          <span className="text-muted-foreground">{label}:</span>
          <span className="font-medium text-foreground">{userLabel}</span>
        </Button>
      );
    });
  }, [assignments]);

  return (
    <div className="flex flex-wrap items-center gap-2">
      <ButtonGroup>
        {leadingAction}
        {summaryButtons}
        {canEdit && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button size="sm" variant="outline" className="gap-2">
                <Pencil className="h-3 w-3" />
                Editar asignaciones
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Asignaciones de roles</DialogTitle>
                <DialogDescription>
                  Selecciona quién está asignado a cada rol para esta solicitud.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                {(Object.keys(ROLE_METADATA) as RoleKey[]).map((role) => (
                  <AssignmentRow
                    key={role}
                    role={role}
                    purchaseRequest={purchaseRequest}
                    currentUser={assignments[role] ?? null}
                    organizationSlug={organizationSlug}
                  />
                ))}
              </div>
            </DialogContent>
          </Dialog>
        )}
      </ButtonGroup>
    </div>
  );
}

interface AssignmentRowProps {
  role: RoleKey;
  purchaseRequest: PurchaseRequest;
  currentUser: UserLite | null;
  organizationSlug: string | null;
}

function AssignmentRow({ role, purchaseRequest, currentUser, organizationSlug }: AssignmentRowProps) {
  const purchaseRequestHref = organizationSlug
    ? `/organizations/${organizationSlug}/purchase_requests/${purchaseRequest.id}`
    : "";
  const [pendingOption, setPendingOption] = useState<AsyncOption | null>(
    currentUser ? userToOption(currentUser) : null,
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setPendingOption(currentUser ? userToOption(currentUser) : null);
  }, [currentUser]);

  const handleSave = useCallback(
    (userId: number | null) => {
      if (saving) return;
      setSaving(true);

      router.patch(
        purchaseRequestHref,
        {
          purchase_request: {
            assignments: {
              [`${role}_id`]: userId,
            },
          },
        },
        {
          preserveScroll: true,
          preserveState: true,
          onFinish: () => setSaving(false),
        },
      );
    },
    [purchaseRequestHref, role, saving],
  );

  const onChange = (option: AsyncOption | null) => {
    setPendingOption(option);
    const targetId = option ? Number(option.value) : null;
    handleSave(targetId);
  };

  const loadOptions = useCallback(
    async (input: string): Promise<AsyncOption[]> => {
      if (!organizationSlug) return [];

      const params = new URLSearchParams();
      if (role !== "requester") {
        params.set("group", role);
      }
      if (input.trim().length > 0) {
        params.set("term", input.trim());
      }

      const response = await fetch(
        `/organizations/${organizationSlug}/members/search?${params.toString()}`,
        {
        headers: {
          Accept: "application/json",
        },
        },
      );
      if (!response.ok) return [];

      const data = (await response.json()) as UserLite[];
      return data.map((user) => userToOption(user));
    },
    [organizationSlug, role],
  );

  const { label, description, icon: Icon } = ROLE_METADATA[role];

  return (
    <div className="space-y-2 rounded-md border p-4">
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-muted-foreground" />
        <div>
          <p className="font-medium">{label}</p>
          <p className="text-xs text-muted-foreground">{description}</p>
        </div>
      </div>
      <AsyncCombobox
        value={pendingOption}
        onChange={onChange}
        loadOptions={loadOptions}
        defaultOptions={pendingOption ? [ pendingOption ] : []}
        placeholder={`Buscar ${label.toLowerCase()}…`}
        buttonClassName="justify-start"
      />
      {pendingOption && (
        <Button
          variant="ghost"
          size="sm"
          className="text-muted-foreground flex items-center gap-2"
          onClick={() => {
            setPendingOption(null);
            handleSave(null);
          }}
          disabled={saving}
        >
          <X className="h-3 w-3" />
          Quitar asignación
        </Button>
      )}
      {saving && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Guardando…
        </div>
      )}
    </div>
  );
}

function formatUserLabel(user: UserLite | null | undefined) {
  if (!user) return "Sin asignar";
  const fullName = user.full_name?.trim();
  if (fullName) {
    return fullName;
  }
  return user.email;
}

function userToOption(user: UserLite): AsyncOption {
  const label = user.full_name?.trim()
    ? `${user.full_name} (${user.email})`
    : user.email;
  return {
    value: user.id,
    label,
  };
}
